import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentSchemaRenderImage } from "scmp-app/components/schema-render/common/image/hooks";
import { ContentSchemaRender } from "scmp-app/components/schema-render/content";
import type { defaultPagesPage$key } from "scmp-app/queries/__generated__/defaultPagesPage.graphql";

import { Pages } from "./consts";
import {
  renderPoliciesAndStandardsSchema,
  renderPromotionsTermsAndConditionsSchema,
} from "./schemas";
import {
  Container,
  ContentContainer,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Headline,
  Paragraph,
  StyledAdSlot,
  StyledAppFooter,
  StyledBr,
  StyledEm,
  StyledHr,
  StyledLi,
  StyledLink,
  StyledOl,
  StyledStrong,
  StyledTable,
  StyledTd,
  StyledUl,
} from "./styles";

type Props = {
  className?: string;
  reference: defaultPagesPage$key;
};
export const DefaultPages: FunctionComponent<Props> = ({ className, reference }) => {
  const page = useFragment(
    graphql`
      fragment defaultPagesPage on Page {
        entityId
        headline
        body {
          json
        }
        ...contentSchemaRenderContent
      }
    `,
    reference,
  );

  const renderSchema = () => {
    switch (page.entityId) {
      case Pages.policiesAndStandards.entityId:
        return renderPoliciesAndStandardsSchema();
      case Pages.promotionsTermsConditions.entityId:
        return renderPromotionsTermsAndConditionsSchema();
      default:
        return null;
    }
  };

  const renderAdSlot = () => {
    if (page.entityId !== Pages.privacyPolicy.entityId) return null;
    return <StyledAdSlot adUnit="privacypolicy/privacypolicypixel" sizes={[[1, 1]]} zone="other" />;
  };

  const { ComponentWithExtraProps: ImageWithExtraProps } = useContentSchemaRenderImage({
    enableResize: true,
  });

  return (
    <>
      {renderSchema()}

      <Container className={className}>
        {renderAdSlot()}
        <ContentContainer>
          <Headline>{page.headline}</Headline>
          <ContentSchemaRender
            extraComponentMap={{
              a: StyledLink,
              b: Paragraph,
              br: StyledBr,
              em: StyledEm,
              h1: Heading1,
              h2: Heading2,
              h3: Heading3,
              h4: Heading4,
              h5: Heading5,
              hr: StyledHr,
              img: ImageWithExtraProps,
              li: StyledLi,
              ol: StyledOl,
              p: Paragraph,
              strong: StyledStrong,
              table: StyledTable,
              td: StyledTd,
              ul: StyledUl,
            }}
            reference={page}
            schema={page.body?.json}
          />
        </ContentContainer>
        <StyledAppFooter variant="home" />
      </Container>
    </>
  );
};

DefaultPages.displayName = "DefaultPages";
